// Endpoint-list bootstrap (docs/04 §2). With no signed list configured the app
// races the same-origin endpoint — `wss://<serving host>/ws` — plus any baked
// mirror hosts (VITE_BOOTSTRAP_MIRRORS, from ci/mirrors.yml), so a cold client
// (fresh install, cleared KV) has more than one address to try instead of
// betting everything on the primary. When VITE_ENDPOINTS_URL / _PUBKEY are
// set, the app additionally fetches a signed list on launch and hands it to
// the engine, which verifies the Ed25519 signature (keyring below) before
// adopting it. The signature is the entire trust story: the `signedBy` id is
// cosmetic, so the keyring trusts the one pinned key for whatever id the
// document names.

import type { EndpointList } from '@merkury/protocol';
import type { Keyring } from '@merkury/brain/call/index.js';
import { BOOTSTRAP_MIRRORS, ENDPOINTS_PUBKEY } from './config.js';

/**
 * Same-origin-plus-mirrors floor list. It is a baked-in trusted seed (never
 * signature-verified — the engine only verifies *updates*), so its signature
 * field is a well-formed placeholder that would fail verification if ever
 * offered as an update, exactly as an untrusted document should.
 *
 * `host`/`secure` are the serving/API origin and always come first (today's
 * behavior, unchanged — callers pass it as `apiHost`, see shell/services.ts
 * and headless-android/main.ts). `mirrorHosts` defaults to the build-time
 * `BOOTSTRAP_MIRRORS` (empty in dev/self-hosted builds, where this collapses
 * to exactly the prior single-entry list); the parameter exists so tests
 * don't have to go through `import.meta.env` to exercise the multi-mirror
 * path. Order is deterministic — primary first, then mirrors in configured
 * order — and a mirror equal to the primary is dropped rather than duplicated.
 *
 * The primary's `secure` flag governs only the primary's own scheme — it is
 * how a self-hosted/dev build reaches its own plain-`http://` origin over
 * `ws://`. Mirrors are always the public production hosts from
 * `ci/mirrors.yml`, which only ever serve TLS, so every mirror is always
 * pinned to `wss://` regardless of `secure`; downgrading them to `ws://`
 * would mean a self-hosted client's plaintext handshake against a
 * third-party production host, carrying its session token in the clear.
 */
export function bootstrapEndpointList(
  host: string,
  secure = true,
  mirrorHosts: readonly string[] = BOOTSTRAP_MIRRORS,
): EndpointList {
  const scheme = secure ? 'wss' : 'ws';
  const mirrors = mirrorHosts.filter((h) => h !== host);
  return {
    version: 1,
    signedBy: 'bootstrap-same-origin',
    signature: 'A'.repeat(86) + '==',
    endpoints: [
      { kind: 'domain', url: `${scheme}://${host}/ws` },
      ...mirrors.map((h) => ({ kind: 'domain' as const, url: `wss://${h}/ws` })),
    ],
  };
}

/**
 * Pinned signer keys the engine trusts for endpoint-list *updates*. Empty when
 * no update source is configured (updates are then impossible — the bootstrap
 * list is the only list). When a pubkey is configured, any `signedBy` resolves
 * to it, so a valid signature under that key is the sole acceptance condition.
 */
export function endpointKeyring(): Keyring {
  const pubkey = ENDPOINTS_PUBKEY;
  if (!pubkey) return {};
  return new Proxy(
    {},
    { get: () => pubkey, has: () => true },
  ) as Keyring;
}
